import { Link } from "react-router-dom";
import { LogoMark } from "@/components/app/Brand";
import { ArrowLeft } from "lucide-react";

const SECTIONS = [
  {
    title: "1. What we collect",
    body: "We collect the information you give us directly — name, email, company, and the project data you create in the Service — plus limited technical data (IP address, browser, device type) needed to operate and secure the Service.",
  },
  {
    title: "2. How we use it",
    body: "We use your information to provide the Service, process payments, respond to support requests, and send account-related communications. With your consent, we may also send product updates. We do not sell your personal data.",
  },
  {
    title: "3. Sharing",
    body: "We share data only with subprocessors who help us run the Service — for example payment processing and cloud hosting — under contracts that require them to protect it. We disclose data if required by law or to protect the rights and safety of CombinedWork and its users.",
  },
  {
    title: "4. Data retention",
    body: "We retain your data for as long as your account is active. After account closure, data is deleted from active systems within a reasonable period, except where retention is required for legal or accounting purposes.",
  },
  {
    title: "5. Your rights",
    body: "Depending on your jurisdiction, you may have the right to access, correct, export, or delete your personal data. You can do most of this yourself from your account settings, or contact us for anything else.",
  },
  {
    title: "6. Security",
    body: "We use industry-standard technical and organisational measures — encryption in transit and at rest, access controls, and regular review — to protect your data against unauthorised access, alteration, or loss.",
  },
  {
    title: "7. Cookies",
    body: "We use essential cookies to keep you signed in and remember your preferences, and limited analytics cookies to understand how the Service is used. You can control non-essential cookies through your browser settings.",
  },
  {
    title: "8. Changes to this policy",
    body: "We may update this policy from time to time. We'll notify active customers of material changes by email or in-app notice before they take effect.",
  },
  {
    title: "9. Contact",
    body: "Questions about this policy or your data? Email us at hello@combinedwork.com. If you need a Data Processing Agreement for your HubSpot/Xero connection, see our DPA.",
  },
];

export default function Privacy() {
  return (
    <div className="bg-white text-gray-900 min-h-screen font-sans">
      <nav className="border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-violet-600 grid place-items-center shadow-md shadow-violet-200">
              <LogoMark size={16} color="#fff" />
            </div>
            <span className="text-base font-bold text-gray-900 tracking-tight">Combined<span className="text-violet-600">Work</span></span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-400 text-sm mb-12">Last updated 9 July 2026</p>

        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="font-semibold text-gray-900 text-base mb-2">{s.title}</h2>
              <p className="text-gray-500 text-sm leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>
      </div>

      <footer className="border-t border-gray-100 py-8 px-6 text-center">
        <span className="text-xs text-gray-400">&copy; {new Date().getFullYear()} CombinedWork. All rights reserved.</span>
      </footer>
    </div>
  );
}
